// app/features/blog/components/BlogReadTracker.tsx
"use client";

import { useEffect, useRef } from "react";

import { HTFButton } from "@/app/shared/components/button/HTFButtonComponent";

import {
  getOrCreateSessionId,
  getOrCreateVisitorId,
  getUtmParams,
} from "../services/visitorIdService";

const READ_ARTICLES_KEY = "htf_read_articles";

interface BlogReadTrackerProps {
  articleSlug: string;
  articleCategory?: string;
  targetKeyword?: string;
}

export function BlogReadTracker({
  articleSlug,
  articleCategory,
  targetKeyword,
}: BlogReadTrackerProps) {
  const hasTracked = useRef(false);

  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;

    const timeoutId = setTimeout(async () => {
      try {
        const readArticles: string[] = JSON.parse(
          localStorage.getItem(READ_ARTICLES_KEY) || "[]",
        );
        const isFirstRead = !readArticles.includes(articleSlug);
        const visitorId = getOrCreateVisitorId();
        const sessionId = getOrCreateSessionId();
        const utmParams = getUtmParams();

        const response = await fetch("/api/blog/track-read", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            article_slug: articleSlug,
            visitor_id: visitorId,
            session_id: sessionId,
            referrer: document.referrer || null,
            is_first_read: isFirstRead,
            article_category: articleCategory || null,
            target_keyword: targetKeyword || null,
            ...utmParams,
          }),
        });

        if (response.ok && isFirstRead) {
          readArticles.push(articleSlug);
          localStorage.setItem(READ_ARTICLES_KEY, JSON.stringify(readArticles));
        }
      } catch (error) {
        console.error("Failed to track blog read:", error);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [articleSlug, articleCategory, targetKeyword]);

  return null;
}

// ---------------------------------------------------------------------------

interface BlogCtaButtonProps {
  articleSlug: string;
  ctaType: "assessment" | "contact" | "newsletter";
  href: string;
  children: React.ReactNode;
  className?: string;
}

export function BlogCtaButton({
  articleSlug,
  ctaType,
  href,
  children,
  className,
}: BlogCtaButtonProps) {
  const handleClick = () => {
    try {
      const visitorId = getOrCreateVisitorId();
      const sessionId = getOrCreateSessionId();
      const utmParams = getUtmParams();

      const payload = JSON.stringify({
        article_slug: articleSlug,
        cta_type: ctaType,
        visitor_id: visitorId,
        session_id: sessionId,
        referrer: document.referrer || null,
        ...utmParams,
      });

      if (navigator.sendBeacon) {
        navigator.sendBeacon("/api/blog/track-cta", payload);
      } else {
        fetch("/api/blog/track-cta", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        });
      }
    } catch (error) {
      console.error("Failed to track CTA click:", error);
    }

    window.location.href = href;
  };

  return (
    <HTFButton
      type="button"
      variant="primary"
      size="lg"
      onClick={handleClick}
      className={className ?? "shadow-xl text-black"}
    >
      {children}
    </HTFButton>
  );
}
